import React, { useState } from 'react';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, DollarSignIcon, CheckCircleIcon, XCircleIcon, CreditCardIcon } from 'lucide-react';
export function VirtualTerminal() {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [reference, setReference] = useState('');
  const formatCardNumber = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add space every 4 digits
    const formatted = digits.replace(/(\d{4})(?=\d)/g, '$1 ');
    return formatted.substring(0, 19); // Limit to 16 digits + 3 spaces
  };
  const formatExpiryDate = (value: string) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as MM/YY
    if (digits.length > 2) {
      return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    }
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Limit to 3-4 digits
    const digits = e.target.value.replace(/\D/g, '');
    setCvv(digits.substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and decimals
    const value = e.target.value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      return;
    }
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Basic validation
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) {
      alert('Please fill in all required fields');
      return;
    }
    // Simulate payment processing
    setProcessingStatus('processing');
    // Generate a random reference number
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    // Simulate API call with timeout
    setTimeout(() => {
      // 80% chance of success for demo purposes
      const isSuccess = Math.random() < 0.8;
      setProcessingStatus(isSuccess ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  return <div className="max-w-3xl mx-auto" data-id="element-9368">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-9369">
        <div className="bg-[#1a2042] px-6 py-4 text-white" data-id="element-9370">
          <h1 className="text-xl font-semibold flex items-center" data-id="element-9371">
            <BanknoteIcon size={24} className="mr-2" data-id="element-9372" />
            Virtual MOTO Terminal
          </h1>
          <p className="text-sm text-gray-300 mt-1" data-id="element-9373">
            Securely process Mail Order / Telephone Order transactions
          </p>
        </div>
        {processingStatus === 'idle' && <form onSubmit={handleSubmit} className="p-6 space-y-6" data-id="element-9374">
            <div className="space-y-4" data-id="element-9375">
              <h2 className="text-lg font-medium text-gray-800 border-b pb-2" data-id="element-9376">
                Payment Details
              </h2>
              <div data-id="element-9377">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9378">
                  Amount*
                </label>
                <div className="relative" data-id="element-9379">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9380">
                    <span className="text-gray-500" data-id="element-9381">RM</span>
                  </div>
                  <input type="text" value={amount} onChange={handleAmountChange} className="pl-12 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" required data-id="element-9382" />
                </div>
              </div>
              <div data-id="element-9383">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9384">
                  Description (Optional)
                </label>
                <input type="text" value={description} onChange={e => setDescription(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Product purchase" data-id="element-9385" />
              </div>
            </div>
            <div className="space-y-4" data-id="element-9386">
              <h2 className="text-lg font-medium text-gray-800 border-b pb-2" data-id="element-9387">
                Card Information
              </h2>
              <div data-id="element-9388">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9389">
                  Card Number*
                </label>
                <div className="relative" data-id="element-9390">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9391">
                    <CreditCardIcon size={16} className="text-gray-400" data-id="element-9392" />
                  </div>
                  <input type="text" value={cardNumber} onChange={handleCardNumberChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="XXXX XXXX XXXX XXXX" required data-id="element-9393" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-9394">
                <div data-id="element-9395">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9396">
                    Expiry Date*
                  </label>
                  <div className="relative" data-id="element-9397">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9398">
                      <CalendarIcon size={16} className="text-gray-400" data-id="element-9399" />
                    </div>
                    <input type="text" value={expiryDate} onChange={handleExpiryDateChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="MM/YY" required data-id="element-9400" />
                  </div>
                </div>
                <div data-id="element-9401">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9402">
                    CVV/CVC*
                  </label>
                  <div className="relative" data-id="element-9403">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9404">
                      <LockIcon size={16} className="text-gray-400" data-id="element-9405" />
                    </div>
                    <input type="text" value={cvv} onChange={handleCvvChange} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="XXX" required data-id="element-9406" />
                  </div>
                </div>
              </div>
              <div data-id="element-9407">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9408">
                  Cardholder Name*
                </label>
                <div className="relative" data-id="element-9409">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9410">
                    <UserIcon size={16} className="text-gray-400" data-id="element-9411" />
                  </div>
                  <input type="text" value={cardholderName} onChange={e => setCardholderName(e.target.value)} className="pl-10 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Name as it appears on card" required data-id="element-9412" />
                </div>
              </div>
            </div>
            <div className="bg-gray-50 p-4 rounded-md text-sm text-gray-600" data-id="element-9413">
              <p className="flex items-center" data-id="element-9414">
                <LockIcon size={14} className="mr-2 text-gray-500" data-id="element-9415" />
                Card information is securely processed and encrypted
              </p>
            </div>
            <div className="flex justify-end" data-id="element-9416">
              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-9417">
                Process Payment
              </button>
            </div>
          </form>}
        {processingStatus === 'processing' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-9418">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600 mb-4" data-id="element-9419"></div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-9420">
              Processing Payment
            </h2>
            <p className="text-gray-600" data-id="element-9421">
              Please wait while we process your transaction...
            </p>
          </div>}
        {processingStatus === 'success' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-9422">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-9423">
              <CheckCircleIcon size={40} className="text-green-600" data-id="element-9424" />
            </div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-9425">
              Payment Successful
            </h2>
            <p className="text-gray-600 mb-2" data-id="element-9426">
              The transaction has been approved.
            </p>
            <div className="bg-gray-100 rounded-md p-4 w-full max-w-md mt-4" data-id="element-9427">
              <p className="text-sm text-gray-700 mb-1" data-id="element-9428">
                <span className="font-medium" data-id="element-9429">Amount:</span> RM
                {parseFloat(amount).toFixed(2)}
              </p>
              <p className="text-sm text-gray-700 mb-1" data-id="element-9430">
                <span className="font-medium" data-id="element-9431">Reference:</span> {reference}
              </p>
              <p className="text-sm text-gray-700 mb-1" data-id="element-9432">
                <span className="font-medium" data-id="element-9433">Card:</span> **** **** ****{' '}
                {cardNumber.slice(-4)}
              </p>
              <p className="text-sm text-gray-700" data-id="element-9434">
                <span className="font-medium" data-id="element-9435">Date:</span>{' '}
                {new Date().toLocaleString()}
              </p>
            </div>
            <button onClick={resetForm} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-9436">
              New Transaction
            </button>
          </div>}
        {processingStatus === 'error' && <div className="p-6 flex flex-col items-center justify-center h-80" data-id="element-9437">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4" data-id="element-9438">
              <XCircleIcon size={40} className="text-red-600" data-id="element-9439" />
            </div>
            <h2 className="text-lg font-medium text-gray-800 mb-2" data-id="element-9440">
              Transaction Failed
            </h2>
            <p className="text-gray-600 mb-2" data-id="element-9441">
              The payment could not be processed.
            </p>
            <div className="bg-red-50 border border-red-100 rounded-md p-4 w-full max-w-md mt-4" data-id="element-9442">
              <p className="text-sm text-red-800" data-id="element-9443">
                Error: Transaction declined by issuing bank. Please try a
                different payment method or contact the card issuer.
              </p>
            </div>
            <button onClick={resetForm} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition-colors" data-id="element-9444">
              Try Again
            </button>
          </div>}
      </div>
    </div>;
}